import { BrowserFeatureError, MatchTransportError, errorMessage } from "./errors";
import { PROTOCOL_VERSION } from "./protocol";

export interface SignalingPeer {
  readonly peerId: string;
  readonly seat: 1 | 2;
}

export interface SignalingJoinedMessage {
  readonly kind: "joined";
  readonly protocolVersion: 1;
  readonly roomCode: string;
  readonly peerId: string;
  readonly seat: 1 | 2;
  readonly resumeToken: string;
  readonly resumed: boolean;
  readonly peers: readonly SignalingPeer[];
  readonly expiresAt: number;
}

export interface SignalingPeerJoinedMessage {
  readonly kind: "peer-joined";
  readonly peerId: string;
  readonly seat: 1 | 2;
  readonly resumed: boolean;
}

export interface SignalingPeerLeftMessage {
  readonly kind: "peer-left";
  readonly peerId: string;
  readonly seat: 1 | 2;
  readonly final: boolean;
  readonly reconnectUntil?: number;
}

export interface SignalingErrorMessage {
  readonly kind: "error";
  readonly code: string;
  readonly message: string;
  readonly recoverable: boolean;
}

interface RelayedSignalBase {
  readonly fromPeerId: string;
  readonly serverReceivedAt?: number;
}

export interface SignalingOfferMessage extends RelayedSignalBase {
  readonly kind: "offer";
  readonly revision: number;
  readonly sdp: string;
}

export interface SignalingAnswerMessage extends RelayedSignalBase {
  readonly kind: "answer";
  readonly revision: number;
  readonly sdp: string;
}

export interface SerializedIceCandidate {
  readonly candidate: string;
  readonly sdpMid?: string | null;
  readonly sdpMLineIndex?: number | null;
  readonly usernameFragment?: string | null;
}

export interface SignalingIceMessage extends RelayedSignalBase {
  readonly kind: "ice";
  readonly revision: number;
  readonly candidate: SerializedIceCandidate | null;
}

export interface SignalingRenegotiateMessage extends RelayedSignalBase {
  readonly kind: "renegotiate";
  readonly revision: number;
  readonly reason?: "negotiationneeded" | "ice-restart";
}

export interface SignalingPingMessage extends RelayedSignalBase {
  readonly kind: "ping";
  readonly nonce: string;
  readonly sentAt?: number;
}

/** Relayed immediately before the authoritative peer-left notification. */
export interface SignalingLeaveMessage extends RelayedSignalBase {
  readonly kind: "leave";
  readonly intent?: "exit" | "rematch";
}

export interface SignalingResumeReservation {
  readonly roomCode: string;
  readonly peerId: string;
  readonly seat: 1 | 2;
  readonly resumeToken: string;
}

export type SignalingServerMessage =
  | SignalingJoinedMessage
  | SignalingPeerJoinedMessage
  | SignalingPeerLeftMessage
  | SignalingErrorMessage
  | SignalingOfferMessage
  | SignalingAnswerMessage
  | SignalingIceMessage
  | SignalingRenegotiateMessage
  | SignalingPingMessage
  | SignalingLeaveMessage;

export interface SignalingCallbacks {
  readonly onJoined?: (message: SignalingJoinedMessage) => void;
  readonly onPeerJoined?: (message: SignalingPeerJoinedMessage) => void;
  readonly onPeerLeft?: (message: SignalingPeerLeftMessage) => void;
  readonly onOffer?: (message: SignalingOfferMessage) => void;
  readonly onAnswer?: (message: SignalingAnswerMessage) => void;
  readonly onIce?: (message: SignalingIceMessage) => void;
  readonly onRenegotiate?: (message: SignalingRenegotiateMessage) => void;
  readonly onPing?: (message: SignalingPingMessage) => void;
  readonly onLeave?: (message: SignalingLeaveMessage) => void;
  readonly onServerError?: (message: SignalingErrorMessage) => void;
  readonly onOpen?: () => void;
  readonly onClose?: (event: CloseEvent) => void;
  readonly onError?: (error: Error) => void;
}

export interface SignalingClientOptions {
  /** Absolute (or browser-relative) `/v1/rooms/{code}/connect` WebSocket URL. */
  readonly websocketUrl: string;
  readonly resumeToken?: string;
  readonly connectTimeoutMs?: number;
  readonly callbacks?: SignalingCallbacks;
  readonly WebSocketClass?: typeof WebSocket;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isSeat(value: unknown): value is 1 | 2 {
  return value === 1 || value === 2;
}

function isPeer(value: unknown): value is SignalingPeer {
  return isRecord(value) && typeof value.peerId === "string" && isSeat(value.seat);
}

function isCandidate(value: unknown): value is SerializedIceCandidate | null {
  return (
    value === null ||
    (isRecord(value) &&
      typeof value.candidate === "string" &&
      (value.sdpMid === undefined || value.sdpMid === null || typeof value.sdpMid === "string") &&
      (value.sdpMLineIndex === undefined ||
        value.sdpMLineIndex === null ||
        typeof value.sdpMLineIndex === "number") &&
      (value.usernameFragment === undefined ||
        value.usernameFragment === null ||
        typeof value.usernameFragment === "string"))
  );
}

export function parseSignalingMessage(value: unknown): SignalingServerMessage {
  if (!isRecord(value) || typeof value.kind !== "string") {
    throw new MatchTransportError("signaling", "Malformed signaling message");
  }
  switch (value.kind) {
    case "joined":
      if (
        value.protocolVersion === PROTOCOL_VERSION &&
        typeof value.roomCode === "string" &&
        typeof value.peerId === "string" &&
        isSeat(value.seat) &&
        typeof value.resumeToken === "string" &&
        typeof value.resumed === "boolean" &&
        Array.isArray(value.peers) &&
        value.peers.every(isPeer) &&
        typeof value.expiresAt === "number" &&
        Number.isFinite(value.expiresAt)
      ) {
        return value as unknown as SignalingJoinedMessage;
      }
      break;
    case "peer-joined":
      if (
        typeof value.peerId === "string" &&
        isSeat(value.seat) &&
        typeof value.resumed === "boolean"
      ) {
        return value as unknown as SignalingPeerJoinedMessage;
      }
      break;
    case "peer-left":
      if (
        typeof value.peerId === "string" &&
        isSeat(value.seat) &&
        typeof value.final === "boolean" &&
        (value.reconnectUntil === undefined ||
          (typeof value.reconnectUntil === "number" && Number.isFinite(value.reconnectUntil)))
      ) {
        return value as unknown as SignalingPeerLeftMessage;
      }
      break;
    case "error":
      if (
        typeof value.code === "string" &&
        typeof value.message === "string" &&
        typeof value.recoverable === "boolean"
      ) {
        return value as unknown as SignalingErrorMessage;
      }
      break;
    case "offer":
    case "answer":
      if (
        typeof value.fromPeerId === "string" &&
        Number.isSafeInteger(value.revision) &&
        typeof value.sdp === "string"
      ) {
        return value as unknown as SignalingOfferMessage | SignalingAnswerMessage;
      }
      break;
    case "ice":
      if (
        typeof value.fromPeerId === "string" &&
        Number.isSafeInteger(value.revision) &&
        isCandidate(value.candidate)
      ) {
        return value as unknown as SignalingIceMessage;
      }
      break;
    case "renegotiate":
      if (
        typeof value.fromPeerId === "string" &&
        Number.isSafeInteger(value.revision) &&
        (value.reason === undefined ||
          value.reason === "negotiationneeded" ||
          value.reason === "ice-restart")
      ) {
        return value as unknown as SignalingRenegotiateMessage;
      }
      break;
    case "ping":
      if (
        typeof value.fromPeerId === "string" &&
        typeof value.nonce === "string" &&
        (value.sentAt === undefined || typeof value.sentAt === "number")
      ) {
        return value as unknown as SignalingPingMessage;
      }
      break;
    case "leave":
      if (
        typeof value.fromPeerId === "string" &&
        (value.intent === undefined || value.intent === "exit" || value.intent === "rematch")
      ) {
        return value as unknown as SignalingLeaveMessage;
      }
      break;
  }
  throw new MatchTransportError(
    "signaling",
    `Invalid signaling ${String(value.kind)} message`,
  );
}

function resolveWebSocketUrl(value: string, resumeToken?: string): string {
  let base: string | undefined;
  if (typeof location !== "undefined") base = location.href;
  let url: URL;
  try {
    url = base === undefined ? new URL(value) : new URL(value, base);
  } catch (error) {
    throw new MatchTransportError("signaling", "Invalid signaling URL", { cause: error });
  }
  if (url.protocol === "http:") url.protocol = "ws:";
  if (url.protocol === "https:") url.protocol = "wss:";
  if (url.protocol !== "ws:" && url.protocol !== "wss:") {
    throw new MatchTransportError("signaling", "Signaling URL must use ws or wss");
  }
  if (resumeToken) url.searchParams.set("resumeToken", resumeToken);
  return url.toString();
}

export class SignalingClient {
  private readonly options: SignalingClientOptions;
  private callbacks: SignalingCallbacks;
  private socket?: WebSocket;
  private joined?: SignalingJoinedMessage;
  private manuallyClosed = false;

  constructor(options: SignalingClientOptions) {
    this.options = options;
    this.callbacks = options.callbacks ?? {};
  }

  setCallbacks(callbacks: SignalingCallbacks): void {
    this.callbacks = callbacks;
  }

  get joinedInfo(): SignalingJoinedMessage | undefined {
    return this.joined;
  }

  get readyState(): number {
    return this.socket?.readyState ?? 3;
  }

  connect(): Promise<SignalingJoinedMessage> {
    if (this.socket && this.socket.readyState <= 1) {
      return this.joined
        ? Promise.resolve(this.joined)
        : Promise.reject(
            new MatchTransportError("invalid-state", "Signaling is already connecting"),
          );
    }
    const WebSocketClass = this.options.WebSocketClass ?? globalThis.WebSocket;
    if (typeof WebSocketClass === "undefined") {
      return Promise.reject(new BrowserFeatureError("WebSocket"));
    }
    this.manuallyClosed = false;

    return new Promise<SignalingJoinedMessage>((resolve, reject) => {
      let settled = false;
      const timeoutMs = this.options.connectTimeoutMs ?? 10_000;
      const timeout = setTimeout(() => {
        if (settled) return;
        settled = true;
        this.socket?.close(4000, "join timeout");
        reject(
          new MatchTransportError("signaling", "Timed out joining signaling room", {
            recoverable: true,
          }),
        );
      }, timeoutMs);

      const finishReject = (error: unknown): void => {
        this.callbacks.onError?.(
          error instanceof Error ? error : new Error(errorMessage(error)),
        );
        if (settled) return;
        settled = true;
        clearTimeout(timeout);
        reject(error);
      };

      try {
        const socket = new WebSocketClass(
          resolveWebSocketUrl(this.options.websocketUrl, this.options.resumeToken),
        );
        this.socket = socket;
        socket.addEventListener("open", () => this.callbacks.onOpen?.());
        socket.addEventListener("message", (event) => {
          try {
            if (typeof event.data !== "string") {
              throw new MatchTransportError("signaling", "Expected a JSON text frame");
            }
            const message = parseSignalingMessage(JSON.parse(event.data) as unknown);
            if (message.kind === "joined") {
              this.joined = message;
              this.callbacks.onJoined?.(message);
              if (!settled) {
                settled = true;
                clearTimeout(timeout);
                resolve(message);
              }
            } else {
              this.dispatch(message);
            }
          } catch (error) {
            finishReject(error);
          }
        });
        socket.addEventListener("error", () => {
          finishReject(
            new MatchTransportError("signaling", "Signaling WebSocket failed", {
              recoverable: true,
            }),
          );
        });
        socket.addEventListener("close", (event) => {
          clearTimeout(timeout);
          this.callbacks.onClose?.(event);
          if (!settled && !this.manuallyClosed) {
            finishReject(
              new MatchTransportError(
                "signaling",
                `Signaling closed before join (${event.code})`,
                { recoverable: true },
              ),
            );
          }
        });
      } catch (error) {
        finishReject(error);
      }
    });
  }

  /** Alias reflecting that opening the room socket is the join operation. */
  join(): Promise<SignalingJoinedMessage> {
    return this.connect();
  }

  private dispatch(message: Exclude<SignalingServerMessage, SignalingJoinedMessage>): void {
    switch (message.kind) {
      case "peer-joined":
        this.callbacks.onPeerJoined?.(message);
        break;
      case "peer-left":
        this.callbacks.onPeerLeft?.(message);
        break;
      case "offer":
        this.callbacks.onOffer?.(message);
        break;
      case "answer":
        this.callbacks.onAnswer?.(message);
        break;
      case "ice":
        this.callbacks.onIce?.(message);
        break;
      case "renegotiate":
        this.callbacks.onRenegotiate?.(message);
        break;
      case "ping":
        this.callbacks.onPing?.(message);
        break;
      case "leave":
        this.callbacks.onLeave?.(message);
        break;
      case "error":
        this.callbacks.onServerError?.(message);
        if (!message.recoverable) {
          this.callbacks.onError?.(
            new MatchTransportError("signaling", message.message, {
              recoverable: false,
            }),
          );
        }
        break;
    }
  }

  private send(frame: Record<string, unknown>): void {
    if (!this.socket || this.socket.readyState !== 1) {
      throw new MatchTransportError("signaling", "Signaling socket is not open", {
        recoverable: true,
      });
    }
    this.socket.send(JSON.stringify(frame));
  }

  sendOffer(sdp: string, revision: number, targetPeerId?: string): void {
    this.send({ kind: "offer", sdp, revision, ...(targetPeerId ? { targetPeerId } : {}) });
  }

  sendAnswer(sdp: string, revision: number, targetPeerId?: string): void {
    this.send({ kind: "answer", sdp, revision, ...(targetPeerId ? { targetPeerId } : {}) });
  }

  sendIce(
    candidate: SerializedIceCandidate | null,
    revision: number,
    targetPeerId?: string,
  ): void {
    this.send({ kind: "ice", candidate, revision, ...(targetPeerId ? { targetPeerId } : {}) });
  }

  requestRenegotiation(
    revision: number,
    reason: "negotiationneeded" | "ice-restart" = "negotiationneeded",
    targetPeerId?: string,
  ): void {
    this.send({ kind: "renegotiate", revision, reason, ...(targetPeerId ? { targetPeerId } : {}) });
  }

  ping(nonce: string, targetPeerId?: string): void {
    this.send({
      kind: "ping",
      nonce,
      sentAt: Date.now(),
      ...(targetPeerId ? { targetPeerId } : {}),
    });
  }

  leave(code = 1000, reason = "leave"): void {
    this.manuallyClosed = true;
    if (this.socket?.readyState === 1) {
      this.send({ kind: "leave" });
    }
    this.socket?.close(code, reason);
    this.socket = undefined;
    this.joined = undefined;
  }

  /**
   * Sends an explicit leave and waits for the Worker's close response. The
   * Worker deletes the reserved seat before closing, so resolution is a safe
   * point to create the next-round room connection.
   */
  async leaveAndWait(options: {
    readonly intent?: "exit" | "rematch";
    readonly timeoutMs?: number;
  } = {}): Promise<SignalingResumeReservation | undefined> {
    const intent = options.intent ?? "exit";
    const timeoutMs = options.timeoutMs ?? 3_000;
    const socket = this.socket;
    const joined = this.joined;
    const reservation =
      intent === "rematch" && joined
        ? {
            roomCode: joined.roomCode,
            peerId: joined.peerId,
            seat: joined.seat,
            resumeToken: joined.resumeToken,
          }
        : undefined;
    if (!socket) {
      this.joined = undefined;
      return undefined;
    }
    if (socket.readyState !== 1) {
      throw new MatchTransportError(
        "signaling",
        "Cannot confirm room leave because signaling is not open",
        { recoverable: true },
      );
    }
    if (!Number.isFinite(timeoutMs) || timeoutMs < 250 || timeoutMs > 15_000) {
      throw new RangeError("leave timeout must be between 250 and 15000 ms");
    }
    this.manuallyClosed = true;
    try {
      await new Promise<void>((resolve, reject) => {
        let settled = false;
        const finish = (error?: Error): void => {
          if (settled) return;
          settled = true;
          clearTimeout(timer);
          socket.removeEventListener("close", onClose);
          if (error) reject(error);
          else resolve();
        };
        const onClose = (event: CloseEvent): void => {
          if (event.code === 1000) finish();
          else {
            finish(
              new MatchTransportError(
                "signaling",
                `Room leave was not confirmed (${event.code})`,
                { recoverable: true },
              ),
            );
          }
        };
        const timer = setTimeout(() => {
          try {
            socket.close(4002, "leave confirmation timeout");
          } catch {
            // The transport may already be gone.
          }
          finish(
            new MatchTransportError(
              "signaling",
              "Timed out waiting for room leave confirmation",
              { recoverable: true },
            ),
          );
        }, timeoutMs);
        socket.addEventListener("close", onClose);
        try {
          socket.send(JSON.stringify({
            kind: "leave",
            ...(intent === "rematch" ? { intent } : {}),
          }));
        } catch (error) {
          finish(
            new MatchTransportError("signaling", "Could not send room leave", {
              recoverable: true,
              cause: error,
            }),
          );
        }
      });
    } finally {
      if (this.socket === socket) this.socket = undefined;
      this.joined = undefined;
    }
    return reservation;
  }
}

export interface CreatedSignalingRoom {
  readonly protocolVersion: 1;
  readonly roomCode: string;
  readonly expiresAt: number;
  readonly websocketUrl: string;
}

export async function createSignalingRoom(
  origin: string,
  fetchImplementation: typeof fetch = globalThis.fetch,
): Promise<CreatedSignalingRoom> {
  if (typeof fetchImplementation !== "function") {
    throw new BrowserFeatureError("WebSocket", "fetch is unavailable for room creation");
  }
  const response = await fetchImplementation(new URL("/v1/rooms", origin), { method: "POST" });
  if (!response.ok) {
    throw new MatchTransportError(
      "signaling",
      `Room creation failed (${response.status})`,
      { recoverable: response.status >= 500 },
    );
  }
  const value = (await response.json()) as unknown;
  if (
    !isRecord(value) ||
    value.protocolVersion !== PROTOCOL_VERSION ||
    typeof value.roomCode !== "string" ||
    typeof value.expiresAt !== "number" ||
    !Number.isFinite(value.expiresAt) ||
    typeof value.websocketUrl !== "string"
  ) {
    throw new MatchTransportError("signaling", "Room service returned an invalid response");
  }
  return value as unknown as CreatedSignalingRoom;
}

export interface TurnCredentials {
  readonly iceServers: readonly RTCIceServer[];
  readonly expiresIn: number;
  /** False when the Worker returns TURN_NOT_CONFIGURED and STUN is used. */
  readonly configured: boolean;
}

export const DEFAULT_STUN_SERVERS: readonly RTCIceServer[] = [
  { urls: "stun:stun.cloudflare.com:3478" },
];

export async function fetchTurnCredentials(
  origin: string,
  room: { readonly roomCode: string; readonly resumeToken: string },
  fetchImplementation: typeof fetch = globalThis.fetch,
): Promise<TurnCredentials> {
  if (typeof fetchImplementation !== "function") {
    throw new BrowserFeatureError("WebSocket", "fetch is unavailable for ICE credentials");
  }
  if (!room.roomCode || !room.resumeToken) {
    throw new MatchTransportError(
      "signaling",
      "TURN credentials require the active room code and resume token",
    );
  }
  const response = await fetchImplementation(new URL("/v1/turn/credentials", origin), {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ roomCode: room.roomCode, resumeToken: room.resumeToken }),
  });
  if (response.status === 501) {
    return { iceServers: DEFAULT_STUN_SERVERS, expiresIn: 0, configured: false };
  }
  if (!response.ok) {
    throw new MatchTransportError(
      "signaling",
      `TURN credential request failed (${response.status})`,
      { recoverable: response.status >= 500 },
    );
  }
  const value = (await response.json()) as unknown;
  if (
    !isRecord(value) ||
    !Array.isArray(value.iceServers) ||
    typeof value.expiresIn !== "number" ||
    !Number.isFinite(value.expiresIn)
  ) {
    throw new MatchTransportError("signaling", "TURN service returned an invalid response");
  }
  return {
    iceServers: value.iceServers as RTCIceServer[],
    expiresIn: value.expiresIn,
    configured: true,
  };
}

export function roomWebSocketUrl(origin: string, roomCode: string): string {
  if (!/^[A-Za-z0-9_-]+$/.test(roomCode)) {
    throw new MatchTransportError("signaling", "Invalid room code");
  }
  const url = new URL(`/v1/rooms/${encodeURIComponent(roomCode)}/connect`, origin);
  url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
  return url.toString();
}
